import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { allowedAuth } from '@/interfaces/common';

import { useTranslations } from '@/hooks/useTranslations';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import InputErrorMsg from '@/components/reUsables/InputErrorMsg';
import LinkedInButton from '@/components/reUsables/LinkedInButton';

const EmailForm = ({
  loading,
  email,
  setEmail,
  handleSubmit,
  fromSignup,
  allowedAuth,
  tenantId,
  errorMsg,
  checked,
  setChecked,
  isCheckedNewsletter,
  setIsCheckedNewsletter,
  shouldShowContinueWithSSOButton,
  ssoLink,
}: {
  loading: boolean;
  email: string;
  setEmail: (email: string) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  fromSignup?: boolean;
  allowedAuth?: allowedAuth;
  tenantId?: string;
  errorMsg?: string;
  checked?: boolean;
  setChecked?: (checked: boolean) => void;
  isCheckedNewsletter?: boolean;
  setIsCheckedNewsletter?: (newsletter: boolean) => void;
  shouldShowContinueWithSSOButton: boolean;
  ssoLink: string;
}) => {
  const { translations, isLoading } = useTranslations('auth_page');

  const email_label = translations?.email_label || 'Email address';
  const email_input_placeholder =
    translations?.email_input_placeholder ||
    'Enter your registered email address';
  const continue_button = translations?.continue_button || 'Continue';
  const donot_have_account =
    translations?.donot_have_account || "Don't have an account?";
  const signup_now = translations?.signup_now || 'Signup Now';
  const newsletter_label =
    translations?.newsletter_label ||
    'I would like to stay informed about the latest news & updates.';
  const company_email_id =
    translations?.company_email_id || 'Enter your company email id';
  const get_started = translations?.get_started || 'Get Started';
  const agree_to_terms =
    translations?.agree_to_terms || "I agree to TenXer Lab's";
  const terms_of_service = translations?.terms_of_service || 'Terms of Service';
  const and = translations?.and || 'and';
  const privacy_policy = translations?.privacy_policy || 'Privacy Policy';
  const or_login_with = translations?.or_login_with || 'or login with';
  const linkedin = translations?.linkedin || 'Linkedin';
  const continue_with_sso =
    translations?.continue_with_sso || 'Continue with SSO';

  const router = useRouter();
  const handleSSOClick = () => {
    if (ssoLink) {
      router.push(ssoLink);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
          <div className="h-10 w-full animate-pulse rounded bg-gray-200" />
        </div>
        <div className="h-10 w-full animate-pulse rounded bg-gray-200" />
        {fromSignup && (
          <>
            <div className="space-y-2">
              <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
              <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <Label htmlFor="email">
          {email_label} <span className="text-red-600">*</span>
        </Label>
        <Input
          className={`mt-2 ${errorMsg && 'border-red-500'}`}
          type="email"
          id="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder={fromSignup ? company_email_id : email_input_placeholder}
        />
        {errorMsg && (
          <div className="my-1">
            <InputErrorMsg text={errorMsg} />
          </div>
        )}

        {fromSignup && (
          <>
            <div className="relative mt-4 flex items-start">
              <Checkbox
                id="tnc"
                checked={checked}
                onCheckedChange={() => setChecked!(!checked)}
              />

              <label htmlFor="tnc">
                <p className="-mt[4px] select-none pl-4 text-xs text-zinc-500">
                  {agree_to_terms}{' '}
                  <a
                    href="https://tenxerlabs.com/terms-conditions/"
                    target="_blank"
                    className="underline"
                  >
                    {terms_of_service}
                  </a>{' '}
                  {and}{' '}
                  <a
                    target="_blank"
                    className="underline"
                    href="https://tenxerlabs.com/privacy-policy/"
                  >
                    {privacy_policy}
                  </a>
                </p>
              </label>
            </div>

            <div className="relative mt-4 flex items-start">
              <Checkbox
                id="newsletter"
                checked={isCheckedNewsletter}
                onCheckedChange={() =>
                  setIsCheckedNewsletter!(!isCheckedNewsletter)
                }
              />

              <label htmlFor="newsletter">
                <p className="-mt[4px] cursor-pointer select-none pl-4 text-xs text-zinc-500">
                  {newsletter_label}
                </p>
              </label>
            </div>
          </>
        )}

        <br />
        <Button
          disabled={(fromSignup && !checked) || loading || email.length === 0}
          type="submit"
          size="fullWidth"
        >
          {loading ? 'Loading...' : fromSignup ? get_started : continue_button}
        </Button>
      </form>

      {shouldShowContinueWithSSOButton && (
        <div>
          <Button
            onClick={handleSSOClick}
            className="mt-2 infineon-primary"
            size="fullWidth"
          >
            {continue_with_sso}
          </Button>
        </div>
      )}

      {!fromSignup && (
        <>
          {allowedAuth?.linkedin && (
            <>
              <div className="my-5 flex items-center gap-3 text-zinc-500">
                <div className="h-[1px] flex-1 border"></div>
                <div className="text-xs">{or_login_with}</div>
                <div className="h-[1px] flex-1 border"></div>
              </div>

              <LinkedInButton text={linkedin} signInPage={true} />
            </>
          )}

          <p className="mt-4 text-center text-xs text-zinc-500">
            {agree_to_terms}{' '}
            <a
              href="https://tenxerlabs.com/terms-conditions/"
              target="_blank"
              className="underline"
            >
              {terms_of_service}
            </a>{' '}
            {and}{' '}
            <a
              target="_blank"
              className="underline"
              href="https://tenxerlabs.com/privacy-policy/"
            >
              {privacy_policy}
            </a>
          </p>

          <p className="mt-5 text-center text-sm font-medium text-zinc-500">
            {donot_have_account}{' '}
            <Link
              className="text-primary-950"
              href={`/signup${tenantId ? `?tenantId=${tenantId}` : ''}`}
            >
              {signup_now}
            </Link>
          </p>
        </>
      )}
    </div>
  );
};

export default EmailForm;
